import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Plus, Trash2, Copy, ArrowLeft, Power, PowerOff, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LeafGlyph } from "@/components/BotanicalBackdrop";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hk_admin_pw";

const emptyBanner = () => ({
  title: "🔥 Limited Time Offer",
  subtitle: "Buy 1 Sea Buckthorn Pure Pulp, Get 1 Free",
  emoji: "🔥",
  price_label: "₹1,299",
  cta_label: "Claim Offer",
  cta_href: "/products/sea-buckthorn-pure-pulp",
  style: "top",
  bg_color: "#1E231F",
  text_color: "#F5EFE4",
  accent_color: "#D97535",
  enabled: true,
  starts_at: "",
  ends_at: "",
  countdown_to: "",
  pages: ["*"],
  priority: 0,
});

const LoginScreen = ({ onLoggedIn }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": pw } });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onLoggedIn(pw);
    } catch {
      setErr("Invalid password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--hk-ivory)] px-6">
      <form onSubmit={submit} noValidate className="w-full max-w-md hk-card p-10" data-testid="admin-banners-login-form">
        <div className="flex items-center gap-2 text-[var(--hk-gold-deep)]"><LeafGlyph /> <span className="label-eyebrow">HerbKranti · Banners</span></div>
        <h1 className="serif mt-4 text-[28px] text-[var(--hk-ink)]">Promotional Banner Console</h1>
        <div className="mt-6 space-y-2">
          <Label className="text-[12px] tracking-[0.15em] uppercase text-[var(--hk-ink-3)]">Admin password</Label>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
            <Input value={pw} onChange={(e) => setPw(e.target.value)} type="password" autoFocus placeholder="••••••••••••"
              className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] h-11 pl-10"
              data-testid="admin-banners-pw-input" />
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
        </div>
        <button type="submit" disabled={busy} className="hk-btn-primary mt-6 w-full justify-center" data-testid="admin-banners-login-btn">
          {busy && <Loader2 size={14} className="animate-spin" />} {busy ? "Verifying" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const BannerEditor = ({ initial, onSave, onDelete, onCancel, busy }) => {
  const [b, setB] = useState(initial);
  const set = (k, v) => setB((x) => ({ ...x, [k]: v }));
  const isEdit = !!initial.id;
  return (
    <div className="hk-card p-6 md:p-8" data-testid="banner-editor">
      <div className="flex items-center justify-between">
        <h3 className="serif text-[22px] text-[var(--hk-ink)]">{isEdit ? "Edit Banner" : "New Banner"}</h3>
        <button onClick={onCancel} className="text-[13px] text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]">Close</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <FieldLabel label="Style">
          <div className="flex flex-wrap gap-2">
            {["top", "bottom", "side"].map((s) => (
              <button key={s} type="button" onClick={() => set("style", s)}
                className={"px-4 py-1.5 rounded-full text-[12px] tracking-widest uppercase border " + (b.style === s ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]" : "bg-white text-[var(--hk-ink-2)] border-[var(--hk-border)] hover:border-[var(--hk-orange)]")}
                data-testid={`banner-style-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </FieldLabel>
        <FieldLabel label="Enabled">
          <button type="button" onClick={() => set("enabled", !b.enabled)}
            className={"inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[12px] " + (b.enabled ? "bg-[#E1EFE4] border-[#B7D6BE] text-[#2F5A38]" : "bg-white border-[var(--hk-border)] text-[var(--hk-ink-3)]")}
            data-testid="banner-enabled-toggle">
            {b.enabled ? <><Power size={12} /> Enabled</> : <><PowerOff size={12} /> Disabled</>}
          </button>
        </FieldLabel>

        <FieldLabel label="Emoji" className="w-24">
          <Input value={b.emoji} onChange={(e) => set("emoji", e.target.value)} placeholder="🔥" data-testid="banner-emoji" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
        <FieldLabel label="Priority (higher wins)">
          <Input type="number" value={b.priority} onChange={(e) => set("priority", Number(e.target.value) || 0)} data-testid="banner-priority" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>

        <FieldLabel label="Title" className="md:col-span-2">
          <Input value={b.title} onChange={(e) => set("title", e.target.value)} data-testid="banner-title" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
        <FieldLabel label="Subtitle" className="md:col-span-2">
          <Input value={b.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="banner-subtitle" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>

        <FieldLabel label="Price Label">
          <Input value={b.price_label} onChange={(e) => set("price_label", e.target.value)} placeholder="₹1,299" data-testid="banner-price" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
        <FieldLabel label="CTA Label">
          <Input value={b.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Claim Offer" data-testid="banner-cta-label" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
        <FieldLabel label="CTA Link / Path" className="md:col-span-2">
          <Input value={b.cta_href} onChange={(e) => set("cta_href", e.target.value)} placeholder="/products/sea-buckthorn-pure-pulp" data-testid="banner-cta-href" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>

        <FieldLabel label="Background Color">
          <ColorInput value={b.bg_color} onChange={(v) => set("bg_color", v)} testId="banner-bg-color" />
        </FieldLabel>
        <FieldLabel label="Text Color">
          <ColorInput value={b.text_color} onChange={(v) => set("text_color", v)} testId="banner-text-color" />
        </FieldLabel>
        <FieldLabel label="Accent Color">
          <ColorInput value={b.accent_color} onChange={(v) => set("accent_color", v)} testId="banner-accent-color" />
        </FieldLabel>
        <FieldLabel label="Countdown to (ISO datetime)">
          <Input type="datetime-local" value={b.countdown_to || ""} onChange={(e) => set("countdown_to", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="banner-countdown" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>

        <FieldLabel label="Starts at (schedule)">
          <Input type="datetime-local" value={b.starts_at || ""} onChange={(e) => set("starts_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="banner-starts" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
        <FieldLabel label="Ends at (schedule)">
          <Input type="datetime-local" value={b.ends_at || ""} onChange={(e) => set("ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="banner-ends" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>

        <FieldLabel label="Pages (comma-separated paths, use * for all)" className="md:col-span-2">
          <Input value={(b.pages || []).join(", ")} onChange={(e) => set("pages", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="* or /products, /products/sea-buckthorn-pure-pulp" data-testid="banner-pages" className="bg-white border-[var(--hk-border)] h-10" />
        </FieldLabel>
      </div>

      {/* Preview */}
      <div className="mt-6">
        <div className="label-eyebrow mb-2">Live Preview</div>
        <div className="rounded-[12px] overflow-hidden border border-[var(--hk-border)]">
          <div style={{ backgroundColor: b.bg_color, color: b.text_color }} className="px-5 py-3.5 flex items-center gap-3 justify-between text-[13px]">
            <div className="flex items-center gap-3 flex-wrap">
              {b.emoji && <span>{b.emoji}</span>}
              <span className="font-medium">{b.title}</span>
              {b.subtitle && <span className="opacity-80 hidden sm:inline">{b.subtitle}</span>}
              {b.price_label && <span className="px-2 py-0.5 rounded-full text-[11px]" style={{ backgroundColor: b.accent_color, color: b.bg_color }}>{b.price_label}</span>}
            </div>
            <span className="px-3 py-1 rounded-full text-[11px] uppercase tracking-widest" style={{ backgroundColor: b.accent_color, color: b.bg_color }}>{b.cta_label}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[var(--hk-border)] flex flex-wrap items-center justify-between gap-3">
        {isEdit && onDelete && (
          <button type="button" onClick={() => onDelete(b.id)} className="inline-flex items-center gap-1 text-[13px] text-red-600 hover:underline" data-testid="banner-delete-btn">
            <Trash2 size={14} /> Delete banner
          </button>
        )}
        <div className="ml-auto flex gap-3">
          <button type="button" onClick={onCancel} className="hk-btn-ghost" data-testid="banner-cancel-btn">Cancel</button>
          <button type="button" disabled={busy} onClick={() => onSave(b)} className="hk-btn-primary" data-testid="banner-save-btn">
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? "Save changes" : "Create banner"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldLabel = ({ label, className = "", children }) => (
  <div className={"space-y-1.5 " + className}>
    <Label className="text-[11px] tracking-[0.18em] uppercase text-[var(--hk-ink-3)]">{label}</Label>
    {children}
  </div>
);

const ColorInput = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-11 h-10 rounded-md border border-[var(--hk-border)] cursor-pointer" data-testid={`${testId}-picker`} />
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="bg-white border-[var(--hk-border)] h-10 flex-1" />
  </div>
);

const BannerList = ({ pw }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | banner object | 'new'
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/banners`, { headers: { "X-Admin-Password": pw } });
      setItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { load(); }, [load]);

  const save = async (b) => {
    setBusy(true);
    try {
      if (b.id) {
        const { id, created_at, ...body } = b;
        await axios.patch(`${API}/banners/${id}`, body, { headers: { "X-Admin-Password": pw } });
        toast.success("Banner updated");
      } else {
        await axios.post(`${API}/banners`, b, { headers: { "X-Admin-Password": pw } });
        toast.success("Banner created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await axios.delete(`${API}/banners/${id}`, { headers: { "X-Admin-Password": pw } });
      toast.success("Deleted");
      setEditing(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggle = async (b) => {
    try {
      await axios.patch(`${API}/banners/${b.id}`, { enabled: !b.enabled }, { headers: { "X-Admin-Password": pw } });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const duplicate = (b) => {
    const { id, created_at, ...rest } = b;
    setEditing({ ...rest, enabled: false, title: `${rest.title} (copy)` });
  };

  if (editing) {
    return (
      <BannerEditor
        initial={editing}
        onSave={save}
        onDelete={editing.id ? remove : null}
        onCancel={() => setEditing(null)}
        busy={busy}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-eyebrow">Banner Library</div>
          <p className="text-[13px] text-[var(--hk-ink-2)] mt-1">
            Schedule and manage all site-wide promotional banners.
          </p>
        </div>
        <button className="hk-btn-primary" onClick={() => setEditing(emptyBanner())} data-testid="banner-new-btn">
          <Plus size={14} /> New Banner
        </button>
      </div>

      {loading ? (
        <div className="hk-card p-12 flex items-center justify-center gap-3 text-[var(--hk-ink-3)]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="hk-card p-16 text-center" data-testid="banner-empty">
          <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-8 h-8 mb-3" />
          <h3 className="serif text-[24px] text-[var(--hk-ink)]">No banners yet.</h3>
          <p className="mt-2 text-[13px] text-[var(--hk-ink-2)]">Create your first promotional banner to run across the site.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="banner-list">
          {items.map((b) => (
            <div key={b.id} className="hk-card p-5 flex flex-wrap items-center gap-4" data-testid={`banner-row-${b.id}`}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-[18px]" style={{ backgroundColor: b.bg_color, color: b.text_color }}>
                {b.emoji || <LeafGlyph className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="serif text-[17px] text-[var(--hk-ink)] truncate">{b.title}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] text-[var(--hk-ink-2)]">{b.style}</span>
                  {b.enabled ? (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E1EFE4] text-[#2F5A38] border border-[#B7D6BE] inline-flex items-center gap-1"><Check size={9} /> Live</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white border border-[var(--hk-border)] text-[var(--hk-ink-3)]">Off</span>
                  )}
                </div>
                {b.subtitle && <div className="text-[12px] text-[var(--hk-ink-2)] truncate mt-1">{b.subtitle}</div>}
                <div className="text-[11px] text-[var(--hk-ink-3)] mt-1">
                  Pages: {(b.pages || []).join(", ") || "*"}
                  {b.starts_at && ` · Starts ${new Date(b.starts_at).toLocaleString("en-IN")}`}
                  {b.ends_at && ` · Ends ${new Date(b.ends_at).toLocaleString("en-IN")}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(b)} className="hk-btn-ghost !py-1.5 !px-3 !text-[11px]" data-testid={`banner-toggle-${b.id}`}>
                  {b.enabled ? "Disable" : "Enable"}
                </button>
                <button onClick={() => duplicate(b)} className="hk-btn-ghost !py-1.5 !px-3 !text-[11px]" data-testid={`banner-duplicate-${b.id}`}>
                  <Copy size={11} /> Duplicate
                </button>
                <button onClick={() => setEditing(b)} className="hk-btn-primary !py-1.5 !px-3 !text-[11px]" data-testid={`banner-edit-${b.id}`}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

import AdminLayout from "@/components/AdminLayout";

const AdminBannersPage = () => {
  usePageTitle("Admin · Banners");
  const pw = sessionStorage.getItem(STORAGE_KEY) || "";
  if (!pw) return null;  // AdminLayout handles auth
  return (
    <AdminLayout title="Banners & Promotions" subtitle="Global announcement bars, promo bars and side banners">
      <BannerList pw={pw} />
    </AdminLayout>
  );
};

export default AdminBannersPage;
